import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, X, Printer } from 'lucide-react';
import { PresentationLogo } from './PresentationLogo';
import { slides, slideTitles } from './slides';
import { useScreenInit } from '../useScreenInit.js';
export function Deck() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.slide === 'number' ? screenInit.slide : 0);
  const [direction, setDirection] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  useEffect(() => {
    if (printMode) {
      // Give the DOM time to finish all Framer Motion entrance animations
      // and load images over the network before triggering the print dialog.
      const timer = setTimeout(() => {
        window.print();
      }, 2500);
      const handleAfterPrint = () => setPrintMode(false);
      window.addEventListener('afterprint', handleAfterPrint);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }
  }, [printMode]);
  useEffect(() => {
    if (printMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentIndex < slides.length - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDirection(-1);
          setCurrentIndex(prev => prev - 1);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      } else if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, printMode]);
  const goTo = (idx: number) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
  };
  const goPrev = () => currentIndex > 0 && goTo(currentIndex - 1);
  const goNext = () => currentIndex < slides.length - 1 && goTo(currentIndex + 1);
  if (printMode) {
    return <>
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center hide-on-print" data-id="element-1" data-mp="2vp2g1yflr6xz">
          <div className="w-12 h-12 border-4 border-[var(--brand)]/20 border-t-[var(--brand)] rounded-full animate-spin mb-4" data-id="element-2" data-mp="2gsl2r1nuri8s" />
          <p className="text-xl font-bold text-[var(--fg)] animate-pulse" data-id="element-3" data-mp="2lqv1fm8sd0z5">
            Preparing PDF...
          </p>
          <p className="text-sm text-[var(--muted)] mt-2" data-id="element-4" data-mp="eyc6j74jljx9">
            Waiting for animations and images to load
          </p>
        </div>
        <div className="print-container bg-white" data-id="element-5" data-mp="24giu302nd3f1">
          {slides.map((Slide, idx) => <div key={idx} className="print-slide w-full h-full relative overflow-hidden break-after-page" data-id="element-6" data-mp="1ziz2kygxuu55" data-mpi={idx}>
              <Slide index={idx} total={slides.length} data-id="element-7" data-mp="2e96ltgersh6w" />
              <PresentationLogo data-id="element-8" data-mp="29fc6sqa40qe5" />
            </div>)}
        </div>
      </>;
  }
  const CurrentSlide = slides[currentIndex];
  return <div className="deck-root relative w-screen h-screen overflow-hidden bg-[var(--bg)]" data-id="element-9" data-mp="2n9uya6oim9ho">
      <AnimatePresence mode="wait" custom={direction} data-id="element-10" data-mp="2j89hia8v1b27">
        <motion.div key={currentIndex} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 40 : -40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -40 : 40
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="absolute inset-0" data-id="element-11" data-mp="2x67qt18bqhau">
          <CurrentSlide index={currentIndex} total={slides.length} data-id="element-12" data-mp="148aurnjs0ya4" />
        </motion.div>
      </AnimatePresence>

      <PresentationLogo data-id="element-13" data-mp="199i6tysycjml" />

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/5 z-40 hide-on-print" data-id="element-14" data-mp="1e705mwd99igv">
        <motion.div className="h-full bg-[var(--brand)]" animate={{
        width: `${(currentIndex + 1) / slides.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-15" data-mp="gjjcbnvznuaj" />
      </div>

      {/* Top left controls */}
      <div className="absolute top-3 left-3 z-50 flex items-center overflow-hidden rounded-full border border-black/10 bg-white/90 shadow-sm backdrop-blur hide-on-print" data-id="element-16" data-mp="v8xot4uv2ru5">
        <button onClick={() => setTocOpen(true)} aria-label="Open table of contents" className="flex h-8 items-center gap-1.5 px-3.5 text-[var(--brand)] transition-colors hover:bg-[var(--surface)]" data-id="element-17" data-mp="3twl9fyvtl1qq">
          <List className="h-3.5 w-3.5" data-id="element-18" data-mp="2c0j7tawbdgli" />
          <span className="text-xs font-semibold" data-id="element-19" data-mp="2byoo1zws3uj0">Contents</span>
        </button>

        <span className="h-4 w-px bg-black/10" aria-hidden="true" data-id="element-20" data-mp="27052i0m3kkr6" />

        <button onClick={() => setPrintMode(true)} aria-label={`Export the ${slides.length}-page deck to PDF`} className="flex h-8 items-center gap-1.5 px-3.5 text-[var(--brand)] transition-colors hover:bg-[var(--surface)]" data-id="element-21" data-mp="223ekh7qqff34">
          <Printer className="h-3.5 w-3.5" data-id="element-22" data-mp="1ob85vq389g5s" />
          <span className="text-xs font-semibold" data-id="element-23" data-mp="1oc9teu1fjv4e">PDF</span>
        </button>
      </div>

      {/* Bottom nav cluster */}
      <div className="absolute bottom-3 right-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-24" data-mp="1jdoqgmusmxc9">
        <button onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-25" data-mp="1ecj0gwsipo6f">
          <ChevronLeft className="h-4 w-4" data-id="element-26" data-mp="qqfc1zxqz8hn" />
        </button>
        <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand)] px-3.5 py-1.5 shadow-md" data-id="element-27" data-mp="qs067og5gpez">
          <span className="text-white font-bold text-sm tabular-nums" data-id="element-28" data-mp="1igtkhgmzrslx">
            {currentIndex + 1}
          </span>
          <span className="text-[var(--brand-lighter)] text-xs font-medium" data-id="element-29" data-mp="1ngfwxnuzx4to">
            / {slides.length}
          </span>
        </div>
        <button onClick={goNext} disabled={currentIndex === slides.length - 1} aria-label="Next slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-30" data-mp="19nq718ccjfra">
          <ChevronRight className="h-4 w-4" data-id="element-31" data-mp="3qw1dswmh5g8p" />
        </button>
      </div>

      {/* Collapsible TOC */}
      <AnimatePresence data-id="element-32" data-mp="3lwotaij2sl9a">
        {tocOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setTocOpen(false)} className="absolute inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-id="element-33" data-mp="4h8ie1ufn48o" />
            <motion.nav initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} aria-label="Table of contents" className="absolute top-0 left-0 bottom-0 z-[70] w-[min(22rem,85vw)] bg-white shadow-2xl flex flex-col" data-id="element-34" data-mp="3hwwg1siqlqxl">
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/5" data-id="element-35" data-mp="2qowdkfb2yjus">
                <div data-id="element-36" data-mp="2vlmpcqfuqtae">
                  <p className="text-[var(--brand)] text-xs font-semibold uppercase tracking-wider" data-id="element-37" data-mp="30efvniqyy9ol">
                    Jascayd · Snakker offer
                  </p>
                  <h2 className="text-[var(--fg)] font-bold text-lg" data-id="element-38" data-mp="35d99xde2y4pt">
                    Contents
                  </h2>
                </div>
                <button onClick={() => setTocOpen(false)} aria-label="Close table of contents" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--surface)] text-[var(--muted)]" data-id="element-39" data-mp="3eegwwzb5bj4l">
                  <X className="h-5 w-5" data-id="element-40" data-mp="1k2ixfp2n21lw" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2" data-id="element-41" data-mp="1p1df2novdk4f">
                {slideTitles.map((s, idx) => {
              const active = idx === currentIndex;
              return <button key={idx} onClick={() => {
                goTo(idx);
                setTocOpen(false);
              }} className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${active ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'}`} data-id="element-42" data-mp="1cnqdf0wzzuex" data-mpi={idx}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--muted)]'}`} data-id="element-43" data-mp="1vgasnfjry109">
                        {idx + 1}
                      </span>
                      <span className="min-w-0" data-id="element-44" data-mp="a26eymbenuqh">
                        <span className={`block text-[0.65rem] font-semibold uppercase tracking-wider ${active ? 'text-[var(--brand-light)]' : 'text-[var(--muted)]'}`} data-id="element-45" data-mp="a1ev28r67lg1">
                          {s.section}
                        </span>
                        <span className={`block text-sm font-semibold truncate ${active ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-46" data-mp="304hxt40y9x13">
                          {s.title}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </div>;
}